import React, { useState, useMemo } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
  Modal,
  Row,
  Col,
  ProgressBar,
  Pagination,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import axios from "../../api/axios";

const ParcelImport = () => {
  const navigate = useNavigate();

  // États
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [items, setItems] = useState([]);
  const [commonMetadata, setCommonMetadata] = useState({});
  const [editableMetadata, setEditableMetadata] = useState({});
  const [modifiedItems, setModifiedItems] = useState({});
  const [deletedItems, setDeletedItems] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [importProgress, setImportProgress] = useState(0);

  // PAGINATION & FILTRES
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("row_number");
  const [sortOrder, setSortOrder] = useState("asc");

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
    missingEmail: 0,
    missingPhone: 0,
    missingName: 0,
    totalVolume: 0,
    totalAmount: 0,
    deleted: 0,
  });

  // Fonctions utilitaires
  const validateEmail = (email) => {
    if (!email) return false;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    const phonePattern = /^\+241[0-9]{8,}$/;
    return phonePattern.test(phone);
  };

  const isValidPhoneWithPrefix = (phone) => {
    if (!phone) return false;
    return phone.startsWith('+241');
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("fr-FR").format(value);
  };

  // ⭐ NORMALISATION DU TELEPHONE - VERSION CORRIGEE
  const normalizePhone = (phone) => {
    if (!phone) return "";
    
    // Convertir en chaine
    let cleaned = String(phone).trim();
    
    // Supprimer .0 a la fin
    cleaned = cleaned.replace(/\.0$/, '');
    
    // Supprimer espaces, points, tirets
    cleaned = cleaned.replace(/[\s\.\-]/g, '');
    
    // Si commence deja par +, laisser
    if (cleaned.startsWith("+")) return cleaned;
    
    // Si commence par 0, enlever 0 et ajouter +241
    if (cleaned.startsWith("0")) {
      return `+241${cleaned.substring(1)}`;
    }
    
    // Si plus de 9 chiffres, considerer comme indicatif
    if (cleaned.length > 9) {
      return `+${cleaned}`;
    }
    
    // Sinon ajouter +241
    return `+241${cleaned}`;
  };

  // Recalculer les statistiques
  const recalculateStats = (itemsList) => {
    const activeItems = itemsList.filter(
      (i) => !deletedItems.includes(i.row_number),
    );

    const validItems = activeItems.filter((i) =>
      validatePhone(i.receiver_phone),
    );
    const missingPhone = activeItems.filter(
      (i) => !i.receiver_phone || !validatePhone(i.receiver_phone),
    ).length;

    const totalVolume = activeItems.reduce((sum, i) => sum + (i.cbm || 0), 0);
    const totalAmount = activeItems.reduce(
      (sum, i) => sum + (i.total_cost || i.calculated_total_cost || 0),
      0,
    );

    setStats({
      total: activeItems.length,
      valid: validItems.length,
      invalid: activeItems.filter(
        (i) => i.receiver_phone && !validatePhone(i.receiver_phone),
      ).length,
      missingEmail: activeItems.filter((i) => !i.receiver_email).length,
      missingPhone: missingPhone,
      missingName: activeItems.filter((i) => !i.receiver_name).length,
      totalVolume: totalVolume,
      totalAmount: totalAmount,
      deleted: deletedItems.length,
    });
  };

  const cleanMetadata = (metadata) => {
    const cleaned = {};
    for (const [key, value] of Object.entries(metadata)) {
      if (value && typeof value === 'string') {
        cleaned[key] = value.replace(/nan\s*/gi, '').trim();
      } else {
        cleaned[key] = value;
      }
    }
    return cleaned;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const extension = selectedFile.name.split(".").pop().toLowerCase();
      if (extension !== "xlsx" && extension !== "xls") {
        toast.error("Format non supporte. Utilisez .xlsx ou .xls");
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Veuillez selectionner un fichier");
      return;
    }

    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("preview_only", "true");

    try {
      const response = await axios.post("/parcels/import/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });

      const data = response.data;
      setPreviewData(data);

      const cleanedMetadata = cleanMetadata(data.common_metadata || {});
      setCommonMetadata(cleanedMetadata);

      const processedItems = (data.items_preview || []).map((item) => ({
        ...item,
        receiver_phone: normalizePhone(item.receiver_phone),
        phone_valid: validatePhone(item.receiver_phone),
        phone_error:
          item.receiver_phone && !validatePhone(item.receiver_phone)
            ? "Format telephone invalide"
            : "",
        email_valid: validateEmail(item.receiver_email),
        email_error:
          item.receiver_email && !validateEmail(item.receiver_email)
            ? "Format email invalide"
            : "",
        cbm: item.cbm || 0,
        weight: item.weight || 0,
        total_cost: item.total_cost || item.calculated_total_cost || 0,
        rate_per_cbm: item.rate_per_cbm || null,
        rate_per_kg: item.rate_per_kg || null,
        parcel_quantity: item.parcel_quantity || 1,
      }));

      setItems(processedItems);

      setEditableMetadata({
        container_number: cleanedMetadata.container_number || "",
        vessel_name: cleanedMetadata.vessel_name || "",
        voyage_number: cleanedMetadata.voyage_number || "",
        shipping_line_name: cleanedMetadata.shipping_line_name || "",
        bill_of_lading: cleanedMetadata.bill_of_lading || "",
        port_of_loading: cleanedMetadata.port_of_loading || "",
        port_of_discharge: cleanedMetadata.port_of_discharge || "",
        destination: cleanedMetadata.destination || "",
        estimated_delivery: cleanedMetadata.estimated_delivery || "",
        mode: cleanedMetadata.mode || "sea",
      });

      setDeletedItems([]);
      recalculateStats(processedItems);
      setCurrentPage(1);
      setStep("preview");

      toast.success(`Fichier analyse : ${processedItems.length} colis trouves`);

      const missingPhones = processedItems.filter(
        (i) => !i.receiver_phone || !validatePhone(i.receiver_phone),
      ).length;
      if (missingPhones > 0) {
        toast.error(
          `${missingPhones} colis n'ont pas de telephone valide.`,
        );
      }
    } catch (err) {
      console.error("Erreur upload:", err);
      if (!err.response) {
        toast.error("Erreur reseau. Verifiez votre connexion");
      } else if (err.response.status === 413) {
        toast.error("Fichier trop volumineux. Maximum 10MB");
      } else if (err.code === "ECONNABORTED") {
        toast.error("Delai depasse. Le fichier est trop volumineux");
      } else {
        toast.error(
          err.response?.data?.error || "Erreur lors de l'analyse du fichier",
        );
      }
      setError(
        err.response?.data?.error || "Erreur lors de l'analyse du fichier",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRow = (item) => {
    setDeletingItem(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deletingItem) {
      const newDeletedItems = [...deletedItems, deletingItem.row_number];
      setDeletedItems(newDeletedItems);

      if (modifiedItems[deletingItem.row_number]) {
        const newModifiedItems = { ...modifiedItems };
        delete newModifiedItems[deletingItem.row_number];
        setModifiedItems(newModifiedItems);
      }

      const activeItems = items.filter(
        (i) => !newDeletedItems.includes(i.row_number),
      );
      recalculateStats(activeItems);
      toast.success(`Ligne ${deletingItem.row_number} supprimee`);

      const remainingItems = items.filter(
        (i) => !newDeletedItems.includes(i.row_number),
      ).length;
      const newTotalPages = Math.ceil(remainingItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    }
    setShowDeleteConfirm(false);
    setDeletingItem(null);
  };

  const openEditModal = (item) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const saveEdit = () => {
    if (!editingItem) return;

    const isValidPhone = validatePhone(editingItem.receiver_phone);
    const isValidEmail = validateEmail(editingItem.receiver_email);

    const updatedItem = {
      ...editingItem,
      phone_valid: isValidPhone,
      phone_error:
        editingItem.receiver_phone && !isValidPhone
          ? "Format telephone invalide"
          : "",
      email_valid: isValidEmail,
      email_error:
        editingItem.receiver_email && !isValidEmail
          ? "Format email invalide"
          : "",
    };

    const mode = editableMetadata.mode || "sea";
    if (mode === "sea" && updatedItem.cbm && updatedItem.rate_per_cbm) {
      updatedItem.total_cost = updatedItem.cbm * updatedItem.rate_per_cbm;
    } else if (
      mode === "air" &&
      updatedItem.weight &&
      updatedItem.rate_per_kg
    ) {
      updatedItem.total_cost = updatedItem.weight * updatedItem.rate_per_kg;
    }

    const updatedItems = items.map((item) =>
      item.row_number === updatedItem.row_number ? updatedItem : item,
    );
    setItems(updatedItems);

    setModifiedItems({
      ...modifiedItems,
      [updatedItem.row_number]: updatedItem,
    });

    recalculateStats(updatedItems);
    setShowEditModal(false);
    toast.success(`Ligne ${updatedItem.row_number} modifiee`);
  };

  // ⭐ CORRECTION: Normalisation automatique dans handleInlineEdit
  const handleInlineEdit = (rowNumber, field, value) => {
    let finalValue = value;
    
    if (field === "receiver_phone") {
      finalValue = normalizePhone(value);
    }

    const updatedItems = items.map((item) => {
      if (item.row_number === rowNumber) {
        const updated = { ...item, [field]: finalValue };
        const mode = editableMetadata.mode || "sea";

        if (field === "receiver_phone") {
          updated.phone_valid = validatePhone(finalValue);
          updated.phone_error =
            finalValue && !validatePhone(finalValue) ? "Format telephone invalide" : "";
        }

        if (mode === "sea" && field === "cbm" && updated.rate_per_cbm) {
          updated.total_cost = updated.cbm * updated.rate_per_cbm;
        } else if (mode === "sea" && field === "rate_per_cbm" && updated.cbm) {
          updated.total_cost = updated.cbm * updated.rate_per_cbm;
        } else if (
          mode === "air" &&
          field === "weight" &&
          updated.rate_per_kg
        ) {
          updated.total_cost = updated.weight * updated.rate_per_kg;
        } else if (
          mode === "air" &&
          field === "rate_per_kg" &&
          updated.weight
        ) {
          updated.total_cost = updated.weight * updated.rate_per_kg;
        }
        return updated;
      }
      return item;
    });

    setItems(updatedItems);
    const updatedItem = updatedItems.find((i) => i.row_number === rowNumber);
    if (updatedItem) {
      setModifiedItems({
        ...modifiedItems,
        [rowNumber]: updatedItem,
      });
    }
    recalculateStats(updatedItems);
  };

  const validateBeforeImport = () => {
    const activeItems = items.filter(
      (item) => !deletedItems.includes(item.row_number),
    );
    const errors = [];

    activeItems.forEach((item) => {
      if (!item.receiver_phone || !validatePhone(item.receiver_phone)) {
        errors.push(
          `Ligne ${item.row_number}: Telephone obligatoire ou invalide`,
        );
      }
      if (item.receiver_email && !validateEmail(item.receiver_email)) {
        errors.push(
          `Ligne ${item.row_number}: Email invalide (${item.receiver_email}) - optionnel`,
        );
      }
    });

    if (errors.length > 0) {
      toast.error(
        <div>
          <strong>Erreur(s) de validation:</strong>
          <ul className="mt-2 mb-0">
            {errors.slice(0, 5).map((err, i) => (
              <li key={i}>{err}</li>
            ))}
            {errors.length > 5 && <li>... et {errors.length - 5} autres</li>}
          </ul>
        </div>,
      );
      return false;
    }
    return true;
  };

  // ⭐ CORRECTION: Normalisation FINALE avant envoi
  const handleValidateImport = async () => {
    const activeItems = items.filter(
      (item) => !deletedItems.includes(item.row_number),
    );

    // ⭐ FORCER LA NORMALISATION DE TOUS LES NUMEROS
    const normalizedItems = activeItems.map(item => ({
      ...item,
      receiver_phone: normalizePhone(item.receiver_phone)
    }));

    // ⭐ VERIFIER QUE TOUS LES NUMEROS ONT +241
    const invalidItems = normalizedItems.filter(
      (item) => item.receiver_phone && !isValidPhoneWithPrefix(item.receiver_phone)
    );

    if (invalidItems.length > 0) {
      const rows = invalidItems.map(i => i.row_number).join(', ');
      toast.error(
        `${invalidItems.length} colis ont un numero sans indicatif (+241) : lignes ${rows}.`
      );
      return;
    }

    // ⭐ METTRE A JOUR items AVEC LES NUMEROS NORMALISES
    setItems(normalizedItems);

    if (!validateBeforeImport()) return;

    setLoading(true);
    setStep("importing");
    setImportProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("preview_only", "false");

    // Ajouter les metadonnees modifiables
    const metadataFields = [
      "container_number",
      "vessel_name",
      "voyage_number",
      "shipping_line_name",
      "bill_of_lading",
      "port_of_loading",
      "port_of_discharge",
      "destination",
      "estimated_delivery",
      "mode",
    ];

    metadataFields.forEach((field) => {
      if (editableMetadata[field]) {
        formData.append(`common_${field}`, editableMetadata[field]);
      }
    });

    // ⭐ UTILISER normalizedItems POUR L'ENVOI
    const filteredModifiedItems = {};
    normalizedItems.forEach((item) => {
      if (modifiedItems[item.row_number]) {
        filteredModifiedItems[item.row_number] = {
          ...modifiedItems[item.row_number],
          receiver_phone: normalizePhone(modifiedItems[item.row_number].receiver_phone)
        };
      }
    });

    if (Object.keys(filteredModifiedItems).length > 0) {
      formData.append("modified_items", JSON.stringify(filteredModifiedItems));
    }

    const progressInterval = setInterval(() => {
      setImportProgress((prev) => Math.min(prev + 5, 90));
    }, 2000);

    try {
      const response = await axios.post("/parcels/import/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000, // ⭐ 10 minutes
      });

      clearInterval(progressInterval);
      setImportProgress(100);
      setImportResult(response.data);
      setStep("done");

      if (response.data.success) {
        toast.success(`Import termine : ${response.data.imported} colis crees`);
      } else {
        toast.error(
          `Import partiel : ${response.data.imported} succes, ${response.data.failed} echecs`,
        );
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error("Erreur import:", err);
      
      if (err.response?.status === 504) {
        toast.error("⏰ Le serveur a mis trop de temps. L'import est peut-etre en cours en arriere-plan.");
      } else {
        toast.error(err.response?.data?.error || "Erreur lors de l'import");
      }
      setStep("preview");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep("upload");
    setFile(null);
    setPreviewData(null);
    setItems([]);
    setModifiedItems({});
    setDeletedItems([]);
    setImportResult(null);
    setError("");
    setCurrentPage(1);
    setSearchTerm("");
    setFilterStatus("all");
  };

  // FILTRAGE ET RECHERCHE
  const filteredItems = useMemo(() => {
    let filtered = items.filter((i) => !deletedItems.includes(i.row_number));

    if (filterStatus === "missing_phone") {
      filtered = filtered.filter(
        (i) => !i.receiver_phone || !validatePhone(i.receiver_phone),
      );
    } else if (filterStatus === "invalid_phone") {
      filtered = filtered.filter(
        (i) => i.receiver_phone && !validatePhone(i.receiver_phone),
      );
    } else if (filterStatus === "valid_phone") {
      filtered = filtered.filter((i) => validatePhone(i.receiver_phone));
    } else if (filterStatus === "missing_email") {
      filtered = filtered.filter((i) => !i.receiver_email);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          (i.receiver_name && i.receiver_name.toLowerCase().includes(term)) ||
          (i.receiver_phone && i.receiver_phone.toLowerCase().includes(term)) ||
          (i.receiver_email && i.receiver_email.toLowerCase().includes(term)) ||
          (i.goods_type && i.goods_type.toLowerCase().includes(term)),
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === "row_number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === "string") aVal = (aVal || "").toLowerCase();
      if (typeof bVal === "string") bVal = (bVal || "").toLowerCase();

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [items, deletedItems, filterStatus, searchTerm, sortBy, sortOrder]);

  // PAGINATION
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const validCount = items.filter(
    (item) =>
      !deletedItems.includes(item.row_number) &&
      validatePhone(item.receiver_phone),
  ).length;
  const mode = editableMetadata.mode || "sea";

  // Rendu upload
  const renderUploadStep = () => (
    <Card className="border-0 shadow-lg rounded-4">
      <Card.Body className="p-5 text-center">
        <div className="display-1 mb-4">📊</div>
        <h3 className="fw-bold mb-3">Import Excel Intelligent</h3>
        <p className="text-muted mb-4">
          Importez vos colis depuis un fichier Excel
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-4">
          <Form.Control
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="d-none"
            id="excel-file-input"
          />
          <Button
            variant="outline-primary"
            size="lg"
            onClick={() => document.getElementById("excel-file-input").click()}
            className="mb-3"
          >
            📁 Choisir un fichier
          </Button>
          {file && (
            <div className="mt-2">
              <Badge bg="success" className="p-2">
                📄 {file.name}
              </Badge>
            </div>
          )}
        </Form.Group>

        <Button
          variant="danger"
          size="lg"
          onClick={handleUpload}
          disabled={!file || loading}
        >
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "🚀 Analyser le fichier"
          )}
        </Button>
      </Card.Body>
    </Card>
  );

  // Rendu preview
  const renderPreviewStep = () => (
    <>
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <h5 className="fw-bold mb-3" style={{ color: "#C62828" }}>
            🚢 Informations du chargement
          </h5>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">Mode</Form.Label>
                <Form.Select
                  size="sm"
                  value={editableMetadata.mode || "sea"}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      mode: e.target.value,
                    })
                  }
                >
                  <option value="sea">🚢 Maritime</option>
                  <option value="air">✈️ Aerien</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📦 Conteneur</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.container_number || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      container_number: e.target.value,
                    })
                  }
                  placeholder="Ex: CACU1234567"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">⛴️ Navire</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.vessel_name || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      vessel_name: e.target.value,
                    })
                  }
                  placeholder="Nom du navire"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">🎫 Voyage</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.voyage_number || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      voyage_number: e.target.value,
                    })
                  }
                  placeholder="Numero de voyage"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">🏢 Compagnie</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.shipping_line_name || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      shipping_line_name: e.target.value,
                    })
                  }
                  placeholder="Nom de la compagnie"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📄 BL No.</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.bill_of_lading || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      bill_of_lading: e.target.value,
                    })
                  }
                  placeholder="Numero BL"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📤 Port chargement</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.port_of_loading || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      port_of_loading: e.target.value,
                    })
                  }
                  placeholder="Port de chargement"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📥 Port dechargement</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.port_of_discharge || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      port_of_discharge: e.target.value,
                    })
                  }
                  placeholder="Port de dechargement"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📍 Destination</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={editableMetadata.destination || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      destination: e.target.value,
                    })
                  }
                  placeholder="Destination finale"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📅 Livraison estimee</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={editableMetadata.estimated_delivery || ""}
                  onChange={(e) =>
                    setEditableMetadata({
                      ...editableMetadata,
                      estimated_delivery: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">📊 Total colis</Form.Label>
                <p className="fw-semibold mb-0">{stats.total}</p>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-muted small">💰 Montant total</Form.Label>
                <p className="fw-semibold text-success mb-0">
                  {formatNumber(stats.totalAmount)} FCFA
                </p>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 bg-success bg-opacity-10 text-center">
            <Card.Body className="py-2">
              <div className="fw-bold text-success">{stats.valid}</div>
              <small className="text-muted">📱 Telephones valides</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 bg-danger bg-opacity-10 text-center">
            <Card.Body className="py-2">
              <div className="fw-bold text-danger">{stats.missingPhone}</div>
              <small className="text-muted">📱 Telephones manquants</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 bg-warning bg-opacity-10 text-center">
            <Card.Body className="py-2">
              <div className="fw-bold text-warning">{stats.missingEmail}</div>
              <small className="text-muted">✉️ Emails manquants</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 bg-info bg-opacity-10 text-center">
            <Card.Body className="py-2">
              <Button variant="link" className="p-0" onClick={handleReset}>
                🔄 Changer de fichier
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {previewData?.warnings?.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <strong>⚠️ Alertes :</strong>
          <ul className="mb-0 mt-2">
            {previewData.warnings.slice(0, 5).map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
            {previewData.warnings.length > 5 && (
              <li>... et {previewData.warnings.length - 5} autres</li>
            )}
          </ul>
        </Alert>
      )}

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <InputGroup size="sm">
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher (nom, telephone, email)..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm("")}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">
                  📋 Tous les colis ({filteredItems.length})
                </option>
                <option value="missing_phone">
                  📱 Telephone manquant ({stats.missingPhone})
                </option>
                <option value="invalid_phone">
                  ❌ Telephone invalide ({stats.invalid})
                </option>
                <option value="valid_phone">
                  ✅ Telephone valide ({stats.valid})
                </option>
                <option value="missing_email">
                  ✉️ Email manquant ({stats.missingEmail})
                </option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10 lignes par page</option>
                <option value={25}>25 lignes par page</option>
                <option value={50}>50 lignes par page</option>
                <option value={100}>100 lignes par page</option>
              </Form.Select>
            </Col>
            <Col md={2} className="text-end">
              <small className="text-muted">
                {filteredItems.length} / {items.length - deletedItems.length} colis
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-lg rounded-4">
        <Card.Header className="bg-white border-0 pt-3">
          <h5 className="fw-bold mb-0">📋 Detail des colis</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: "550px", overflowY: "auto" }}>
            <Table hover className="mb-0" size="sm">
              <thead className="bg-light sticky-top">
                <tr>
                  <th style={{ cursor: "pointer", width: "50px" }} onClick={() => handleSort("row_number")}>
                    # {sortBy === "row_number" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("receiver_name")}>
                    Destinataire {sortBy === "receiver_name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => handleSort("receiver_phone")}>
                    📱 Telephone {sortBy === "receiver_phone" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th>✉️ Email (optionnel)</th>
                  <th>Type</th>
                  <th>Qte</th>
                  <th>CBM (m³)</th>
                  <th>Tarif CBM</th>
                  <th>Total (FCFA)</th>
                  <th>Statut</th>
                  <th style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => {
                  const isValidPhone = validatePhone(item.receiver_phone);
                  const hasPhone = item.receiver_phone && item.receiver_phone !== "";
                  const isValidEmail = validateEmail(item.receiver_email);

                  return (
                    <tr
                      key={item.row_number}
                      className={
                        !hasPhone || !isValidPhone
                          ? "table-danger"
                          : modifiedItems[item.row_number] ? "table-info" : ""
                      }
                    >
                      <td className="fw-bold">{item.row_number}</td>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={item.receiver_name || ""}
                          onChange={(e) =>
                            handleInlineEdit(item.row_number, "receiver_name", e.target.value)
                          }
                          placeholder="Nom"
                          style={{ minWidth: "150px" }}
                        />
                      </td>
                      <td className={!hasPhone || !isValidPhone ? "bg-danger bg-opacity-25" : ""}>
                        <Form.Control
                          type="tel"
                          size="sm"
                          value={item.receiver_phone || ""}
                          onChange={(e) =>
                            handleInlineEdit(item.row_number, "receiver_phone", e.target.value)
                          }
                          placeholder="Telephone (OBLIGATOIRE)"
                          isInvalid={hasPhone && !isValidPhone}
                          style={{ minWidth: "140px" }}
                        />
                        <Form.Control.Feedback type="invalid">
                          Format invalide (ex: +241612345678)
                        </Form.Control.Feedback>
                      </td>
                      <td>
                        <Form.Control
                          type="email"
                          size="sm"
                          value={item.receiver_email || ""}
                          onChange={(e) =>
                            handleInlineEdit(item.row_number, "receiver_email", e.target.value)
                          }
                          placeholder="Email (optionnel)"
                          isInvalid={item.receiver_email && !isValidEmail}
                          style={{ minWidth: "180px" }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={item.goods_type || ""}
                          onChange={(e) =>
                            handleInlineEdit(item.row_number, "goods_type", e.target.value)
                          }
                          placeholder="Type"
                          style={{ minWidth: "100px" }}
                        />
                      </td>
                      <td style={{ minWidth: "70px" }}>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={item.parcel_quantity || 1}
                          onChange={(e) =>
                            handleInlineEdit(
                              item.row_number,
                              "parcel_quantity",
                              parseInt(e.target.value) || 1
                            )
                          }
                        />
                      </td>
                      <td style={{ minWidth: "80px" }}>
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={item.cbm || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            handleInlineEdit(item.row_number, "cbm", val);
                          }}
                        />
                      </td>
                      <td style={{ minWidth: "100px" }}>
                        <Form.Control
                          type="number"
                          step="1000"
                          size="sm"
                          value={item.rate_per_cbm || ""}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || null;
                            handleInlineEdit(item.row_number, "rate_per_cbm", val);
                          }}
                          placeholder="Tarif"
                        />
                      </td>
                      <td className="fw-bold text-success" style={{ minWidth: "120px" }}>
                        {item.total_cost > 0 ? `${formatNumber(item.total_cost)} FCFA` : "-"}
                      </td>
                      <td>
                        {!hasPhone || !isValidPhone ? (
                          <Badge bg="danger">📱 Telephone requis</Badge>
                        ) : (
                          <Badge bg="success">✓ WhatsApp pret</Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openEditModal(item)}
                            title="Modifier"
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteRow(item)}
                            title="Supprimer"
                          >
                            🗑️
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer className="bg-white border-0 py-3">
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="text-muted small">
                Affichage {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredItems.length)} sur{" "}
                {filteredItems.length} colis
              </div>
              <Pagination size="sm" className="mb-0">
                <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={prevPage} disabled={currentPage === 1} />
                {getPageNumbers().map((page) => (
                  <Pagination.Item key={page} active={page === currentPage} onClick={() => paginate(page)}>
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next onClick={nextPage} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">
              {stats.deleted > 0 && (
                <span className="text-danger">🗑️ {stats.deleted} ligne(s) supprimee(s)</span>
              )}
              {modifiedItems && Object.keys(modifiedItems).length > 0 && (
                <span className="text-info ms-3">
                  ✏️ {Object.keys(modifiedItems).length} ligne(s) modifiee(s)
                </span>
              )}
            </div>
            <div className="d-flex gap-3">
              <Button variant="secondary" onClick={handleReset}>
                Annuler
              </Button>
              <Button
                variant="danger"
                onClick={handleValidateImport}
                disabled={validCount === 0 || loading}
                className="px-4"
              >
                {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                {loading ? "Import..." : `✅ Importer (${validCount} avec telephone)`}
              </Button>
            </div>
          </div>
        </Card.Footer>
      </Card>
    </>
  );

  const renderDeleteConfirmModal = () => (
    <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>🗑️ Confirmer la suppression</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Etes-vous sur de vouloir supprimer cette ligne ?</p>
        {deletingItem && (
          <div className="bg-light p-3 rounded">
            <strong>Ligne {deletingItem.row_number}</strong>
            <br />
            Destinataire: {deletingItem.receiver_name || "-"}
            <br />
            Telephone: {deletingItem.receiver_phone || "-"}
            <br />
            Email: {deletingItem.receiver_email || "-"}
          </div>
        )}
        <p className="text-danger mt-3 small">
          ⚠️ Cette action est irreversible.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
          Annuler
        </Button>
        <Button variant="danger" onClick={confirmDelete}>
          🗑️ Supprimer definitivement
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderImportingStep = () => (
    <Card className="border-0 shadow-lg rounded-4 text-center p-5">
      <Spinner animation="border" style={{ color: "#C62828" }} size="lg" className="mb-4" />
      <h4 className="fw-bold mb-3">Import en cours...</h4>
      <p className="text-muted mb-4">Creation des comptes clients et des colis</p>
      <ProgressBar
        now={importProgress}
        label={`${importProgress}%`}
        variant="danger"
        className="mb-3"
        style={{ height: "30px" }}
      />
      <small className="text-muted">Veuillez patienter...</small>
    </Card>
  );

  const renderDoneStep = () => (
    <Card className="border-0 shadow-lg rounded-4">
      <Card.Body className="p-5 text-center">
        <div className="display-1 mb-3">✅</div>
        <h3 className="fw-bold mb-3">Import termine !</h3>
        <Row className="mt-4">
          <Col md={4}>
            <div className="display-5 fw-bold text-success">{importResult?.imported || 0}</div>
            <div className="text-muted">Colis importes</div>
          </Col>
          <Col md={4}>
            <div className="display-5 fw-bold text-danger">{importResult?.failed || 0}</div>
            <div className="text-muted">Echecs</div>
          </Col>
          <Col md={4}>
            <div className="display-5 fw-bold text-info">
              {(importResult?.created_users?.length || 0) + (importResult?.existing_users?.length || 0)}
            </div>
            <div className="text-muted">Comptes clients</div>
          </Col>
        </Row>
        {importResult?.errors?.length > 0 && (
          <Alert variant="danger" className="mt-4 text-start">
            <strong>❌ Erreurs :</strong>
            <ul className="mb-0 mt-2">
              {importResult.errors.slice(0, 5).map((err, idx) => (
                <li key={idx}>Ligne {err.row} : {err.receiver} - {err.error}</li>
              ))}
            </ul>
          </Alert>
        )}
        <div className="mt-5">
          <Button variant="secondary" onClick={handleReset} className="me-3">
            📁 Nouvel import
          </Button>
          <Button variant="danger" onClick={() => navigate("/operator")}>
            📊 Dashboard
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderEditModal = () => (
    <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>✏️ Modifier le colis - Ligne {editingItem?.row_number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {editingItem && (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nom du destinataire</Form.Label>
              <Form.Control
                type="text"
                value={editingItem.receiver_name || ""}
                onChange={(e) => setEditingItem({ ...editingItem, receiver_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>📱 Telephone *</Form.Label>
              <Form.Control
                type="tel"
                value={editingItem.receiver_phone || ""}
                onChange={(e) =>
                  setEditingItem({
                    ...editingItem,
                    receiver_phone: normalizePhone(e.target.value),
                  })
                }
                placeholder="+241612345678"
                isInvalid={editingItem.receiver_phone && !validatePhone(editingItem.receiver_phone)}
              />
              <Form.Control.Feedback type="invalid">
                Format invalide (ex: +241612345678)
              </Form.Control.Feedback>
              <Form.Text className="text-muted">Obligatoire pour WhatsApp</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>✉️ Email (optionnel)</Form.Label>
              <Form.Control
                type="email"
                value={editingItem.receiver_email || ""}
                onChange={(e) => setEditingItem({ ...editingItem, receiver_email: e.target.value })}
                placeholder="client@email.com"
                isInvalid={editingItem.receiver_email && !validateEmail(editingItem.receiver_email)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Type de marchandise</Form.Label>
              <Form.Control
                type="text"
                value={editingItem.goods_type || ""}
                onChange={(e) => setEditingItem({ ...editingItem, goods_type: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Volume (CBM)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={editingItem.cbm || ""}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setEditingItem({ ...editingItem, cbm: val });
                      if (editingItem.rate_per_cbm) {
                        setEditingItem((prev) => ({
                          ...prev,
                          total_cost: val * (prev.rate_per_cbm || 0),
                        }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tarif au CBM (FCFA)</Form.Label>
                  <Form.Control
                    type="number"
                    step="1000"
                    value={editingItem.rate_per_cbm || ""}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setEditingItem({ ...editingItem, rate_per_cbm: val });
                      if (editingItem.cbm) {
                        setEditingItem((prev) => ({
                          ...prev,
                          total_cost: (prev.cbm || 0) * val,
                        }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Alert variant="info">
              ℹ️ Le telephone est OBLIGATOIRE pour WhatsApp.
            </Alert>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
          Annuler
        </Button>
        <Button variant="danger" onClick={saveEdit}>
          Enregistrer
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <Container className="py-4" fluid="lg">
      {step === "upload" && renderUploadStep()}
      {step === "preview" && renderPreviewStep()}
      {step === "importing" && renderImportingStep()}
      {step === "done" && renderDoneStep()}
      {renderEditModal()}
      {renderDeleteConfirmModal()}
    </Container>
  );
};

export default ParcelImport;
